import React from 'react'
import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title?: string
  description?: string
  canonical?: string
  ogImage?: string
  ogType?: string
  twitterCard?: string
  structuredData?: Record<string, unknown>
  author?: string
  robots?: string
  alternateLanguages?: Array<{ hreflang: string; href: string }>
}

const SEO: React.FC<SEOProps> = ({
  title = 'TherapyLink - Special Education Therapy Provider Matching',
  description = 'TherapyLink helps schools, NPAs, and therapy teams connect special education students with qualified speech, occupational, physical, and behavioral therapy providers.',
  canonical,
  ogImage = 'https://therapy-link.org/images/therapy-1.png',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData,
  author = 'TherapyLink',
  robots = 'index, follow',
  alternateLanguages = [],
}) => {
  const siteUrl = 'https://therapy-link.org'
  const fullTitle = title.includes('TherapyLink') ? title : `${title} | TherapyLink`

  // Default structured data for the service offering.
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'TherapyLink',
    serviceType: 'Special education therapy provider matching platform',
    description,
    url: siteUrl,
    image: ogImage,
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Schools, special education teams, NPAs, and therapy provider organizations',
    },
    provider: {
      '@type': 'Organization',
      name: 'TherapyLink',
      url: siteUrl,
      logo: `${siteUrl}/images/tl-512.png`,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'info@therapy-link.org',
      availableLanguage: ['English'],
    },
  }

  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'TherapyLink',
    url: siteUrl,
    logo: `${siteUrl}/images/tl-512.png`,
    description:
      'Special education therapy provider matching and workflow platform for schools, NPAs, and therapy organizations.',
    foundingDate: '2024',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'info@therapy-link.org',
    },
  }

  const websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'TherapyLink',
    url: siteUrl,
    publisher: {
      '@type': 'Organization',
      name: 'TherapyLink',
      logo: `${siteUrl}/images/tl-512.png`,
    },
  }

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
    ],
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="author" content={author} />
      <meta name="robots" content={robots} />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Alternate Languages */}
      {alternateLanguages.map(lang => (
        <link key={lang.hreflang} rel="alternate" hrefLang={lang.hreflang} href={lang.href} />
      ))}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical || siteUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content="TherapyLink Platform" />
      <meta property="og:site_name" content="TherapyLink" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="TherapyLink Platform" />
      <meta name="twitter:site" content="@therapylink" />

      {/* Additional SEO Meta Tags */}
      <meta name="generator" content="React" />
      <meta name="rating" content="General" />
      <meta name="referrer" content="origin-when-cross-origin" />
      <meta name="format-detection" content="telephone=no" />
      <meta httpEquiv="x-dns-prefetch-control" content="on" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
      <script type="application/ld+json">{JSON.stringify(organizationStructuredData)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteStructuredData)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbStructuredData)}</script>
    </Helmet>
  )
}

export default SEO
